import { useState } from 'react';

import { Box, Paper, SxProps, Theme, Typography } from '@mui/material';

import { useLanguage } from '../../../hooks';
import { ClockOutline, CrownSimple, FileOutline, ListChecks, SmartphoneOutline } from '../../../icons';
import { useUserDataStore } from '../../../newStore/userDataStore';
import { DemoCarouselNavButtons } from '../../uiComponents/Pricing/DemoCarouselNavButtons';
import { DemoCarouselSteps } from '../../uiComponents/Pricing/DemoCarouselSteps';
import { PricingCTAButtons } from '../../uiComponents/Pricing/PricingCTAButtons';

// The illustration and the web/app screenshots bake localized UI text into
// their content, so each needs one asset per account language. They live on
// S3 (same convention as PlanningsLanding), localized per account language:
// `{name}-{es|en|pt}.{svg|png}`.
const S3_BASE = 'https://data-scope.s3.us-west-2.amazonaws.com/assets/task-assigns';
const SUPPORTED_ASSET_LANGS = ['es', 'en', 'pt'];

// Module landing for accounts whose plan does NOT include Tareas asignadas
// (Free and Starter). Mirrors the structure and voice of the Plannings
// landing (lang.plannings.pricing.landing / PlanningsLanding.tsx): outcome
// title, "Con esta funcionalidad es posible", value bullets, illustration on
// the right, Paper demo carousel below. Unlike Plannings, this module's
// backend gate is binary (no quota) -- the caller only renders this when the
// initial data fetch returned 403 `feature-not-in-plan`, not from a
// `plan_access` field on a 200 response.
const customStyles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '60px',
    marginBottom: '45px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '520px',
  },
  tag: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#531DAB',
    backgroundColor: '#F9F0FF',
    border: '1px solid #D3ADF7',
    borderRadius: '4px',
    padding: '4px 10px',
    marginBottom: '12px',
  },
  tagIcon: {
    fontSize: '16px',
    color: '#531DAB',
  },
  title: {
    fontSize: '30px',
    fontWeight: 600,
    lineHeight: '40px',
    color: '#4760F6',
    marginBottom: '20px',
  },
  intro: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#3B3C40',
    marginBottom: '20px',
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  feature: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#3B3C40',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  featureIconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featureIcon: {
    fontSize: '24px',
  },
  illustration: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  illustrationSvg: {
    width: '100%',
    maxWidth: '480px',
    height: 'auto',
  },
  paper: {
    padding: '45px',
    borderRadius: '20px',
    boxShadow: '0px 2.1px 8.39px 0px rgba(0, 0, 0, 0.09)',
    marginBottom: '45px',
  },
  demoBlockContainer: {
    display: 'flex',
    gap: '40px',
    alignItems: 'center',
    '@media (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  // Fixed height (matches the web screenshot at its natural 464px-wide size)
  // so switching between the Web and App demo tabs never resizes the Paper --
  // the two portrait app screenshots would otherwise render ~445px tall at
  // 200px wide, taller than the text column next to them, pushing the whole
  // card (and page) past the viewport and forcing a scroll.
  demoImage: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '32px',
    flexWrap: 'wrap',
    height: '348px',
  },
  demoImgSingle: {
    width: '464px',
    maxWidth: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '16px',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.09)',
  },
  demoImgDouble: {
    width: '200px',
    maxWidth: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
    borderRadius: '16px',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.09)',
  },
  demoContent: {
    flex: 1.2,
  },
  demoTitle: {
    fontSize: '24px',
    fontWeight: 600,
    lineHeight: '32px',
    color: '#4760F6',
    marginBottom: '10px',
  },
  demoCard: {
    display: 'flex',
    gap: '15px',
    padding: '28px',
    borderRadius: '20px',
    backgroundColor: '#F0F5FF',
    flexDirection: 'column',
    width: '100%',
  },
  demoIcon: {
    width: '32px',
    height: '32px',
    color: (theme) => theme.palette.primary.main,
  },
  demoCardTitle: {
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: '28px',
    color: '#3B3C40',
  },
  demoCardDescription: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    color: '#3B3C40',
  },
} satisfies Record<string, SxProps<Theme>>;

// Value bullets: icon + accent color are presentation, not content -- the
// bullet copy itself comes from i18n (landingLang.bullet1/2/3) like Plannings.
const FEATURE_ICONS = [
  { Icon: ListChecks, background: '#F0F5FF', color: '#3E8FFA' },
  { Icon: ClockOutline, background: '#FFF2E8', color: '#FA541C' },
  { Icon: FileOutline, background: '#F9F0FF', color: '#722ED1' },
];

enum TaskAssignsDemo {
  WEB,
  APP,
}

interface Props {
  onKnowPlans: () => void;
  onContactSales: () => void;
}

export function TaskAssignsLanding({ onKnowPlans, onContactSales }: Props) {
  const lang = useLanguage();
  const landingLang = lang.task_assigner.pricing.landing;
  const langCode = useUserDataStore((state) => state.lang) || 'es';
  const assetLang = SUPPORTED_ASSET_LANGS.includes(langCode) ? langCode : 'es';
  const ilustracionImg = `${S3_BASE}/ilustracion-tareas-${assetLang}.svg`;
  const tablaImg = `${S3_BASE}/tabla-tareas-${assetLang}.png`;
  const app1Img = `${S3_BASE}/tareas-app1-${assetLang}.png`;
  const app2Img = `${S3_BASE}/tareas-app2-${assetLang}.png`;
  const [showingDemo, setShowingDemo] = useState<TaskAssignsDemo>(TaskAssignsDemo.WEB);
  const bullets = [landingLang.bullet1, landingLang.bullet2, landingLang.bullet3];

  return (
    <Box>
      <Box sx={customStyles.header}>
        <Box sx={customStyles.headerContent}>
          <Typography sx={customStyles.tag}>
            <CrownSimple sx={customStyles.tagIcon} />
            {landingLang.availability}
          </Typography>
          <Typography sx={customStyles.title}>{landingLang.title}</Typography>
          <Typography sx={customStyles.intro}>{landingLang.intro}</Typography>
          <Box
            sx={customStyles.features}
            component='ul'
          >
            {bullets.map((bullet, i) => {
              const { Icon, background, color } = FEATURE_ICONS[i];
              return (
                <Typography
                  key={bullet}
                  component='li'
                  sx={customStyles.feature}
                >
                  <Box sx={[customStyles.featureIconBox, { backgroundColor: background }]}>
                    <Icon sx={[customStyles.featureIcon, { color }]} />
                  </Box>
                  {bullet}
                </Typography>
              );
            })}
          </Box>
          <PricingCTAButtons
            knowPlansLabel={landingLang.knowPlans}
            contactSalesLabel={landingLang.contactSales}
            onKnowPlans={onKnowPlans}
            onContactSales={onContactSales}
          />
        </Box>
        <Box sx={customStyles.illustration}>
          <Box
            component='img'
            src={ilustracionImg}
            alt={landingLang.illustrationAlt}
            sx={customStyles.illustrationSvg}
          />
        </Box>
      </Box>

      <Paper sx={customStyles.paper}>
        <Box sx={customStyles.demoBlockContainer}>
          <Box sx={customStyles.demoImage}>
            {showingDemo === TaskAssignsDemo.WEB ? (
              <Box
                component='img'
                src={tablaImg}
                alt={landingLang.webImageAlt}
                sx={customStyles.demoImgSingle}
              />
            ) : (
              <>
                <Box
                  component='img'
                  src={app1Img}
                  alt={landingLang.appImageAlt}
                  sx={customStyles.demoImgDouble}
                />
                <Box
                  component='img'
                  src={app2Img}
                  alt={landingLang.appImageAlt}
                  sx={customStyles.demoImgDouble}
                />
              </>
            )}
          </Box>
          <Box sx={customStyles.demoContent}>
            <DemoCarouselSteps
              activeIndex={showingDemo}
              onChangeIndex={(index) => setShowingDemo(index)}
              testIds={['demo-step-web', 'demo-step-app']}
            />
            <Typography sx={customStyles.demoTitle}>{landingLang.demoTitle}</Typography>
            <Box sx={customStyles.demoCard}>
              {showingDemo === TaskAssignsDemo.APP ? (
                <SmartphoneOutline sx={customStyles.demoIcon} />
              ) : (
                <ListChecks sx={customStyles.demoIcon} />
              )}
              <Typography sx={customStyles.demoCardTitle}>
                {showingDemo === TaskAssignsDemo.APP ? landingLang.demoAppTitle : landingLang.demoWebTitle}
              </Typography>
              <Typography sx={customStyles.demoCardDescription}>
                {showingDemo === TaskAssignsDemo.APP ? landingLang.demoAppBody : landingLang.demoWebBody}
              </Typography>
            </Box>
            <DemoCarouselNavButtons
              activeIndex={showingDemo}
              onChangeIndex={(index) => setShowingDemo(index)}
              previousLabel={landingLang.previous}
              nextLabel={landingLang.next}
              marginTop='16px'
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
